import { AsyncPipe, DatePipe, NgFor, NgIf } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PrimeTemplate } from 'primeng/api';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { SkeletonModule } from 'primeng/skeleton';
import { Table, TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { DatePickerModule } from 'primeng/datepicker';
import { DrawerModule } from 'primeng/drawer';
import { SelectModule } from 'primeng/select';
import { Subscription } from 'rxjs';
import { ExcelService } from 'src/app/core/services/excel.service';
import { ExcelRequestColumn } from 'src/app/models/ExcelRequestColumn';
import { PersonQuery } from 'src/app/models/FilesRequest';
import { FilterResponse } from 'src/app/models/FilterResponse';
import { QrCodeDialogComponent } from 'src/app/shared/components/qr-code-dialog/qr-code-dialog.component';
import { LoadingComponent } from 'src/app/utils/loading/loading.component';
import { UtilsService } from 'src/app/utils/utils.service';
import { ColumnFilterDirective } from '../../../shared/directives/p-columnFilter.directive';
import { TableDirective } from '../../../shared/directives/p-table.directive';
import { NewStandardRequestComponent } from '../new-standard-request/new-standard-request.component';
import { NewStandardRequestService } from '../new-standard-request/new-standard-request.service';
import { StandardRequestDetailService } from '../standard-request-detail/standard-request-detail.service';
import { StandardRequestService } from './standard-request.service';
import { StandardRequestsActiveFiltersComponent } from './standard-requests-active-filters/standard-requests-active-filters.component';
import { StandardRequestsFilterService } from './standard-requests-filter.service';

@Component({
    selector: 'app-standard-requests',
    templateUrl: './standard-requests.component.html',
    standalone: true,
    imports: [
        NgIf,
        DrawerModule,
        PrimeTemplate,
        NewStandardRequestComponent,
        FormsModule,
        ReactiveFormsModule,
        TableModule,
        TableDirective,
        TooltipModule,
        StandardRequestsActiveFiltersComponent,
        IconFieldModule,
        InputIconModule,
        InputTextModule,
        ColumnFilterDirective,
        SelectModule,
        DatePickerModule,
        NgFor,
        SkeletonModule,
        TranslateModule,
        AsyncPipe,
        DatePipe,
        LoadingComponent,
        QrCodeDialogComponent
    ]
})
export class StandardRequestsComponent implements OnInit, OnDestroy {

    isLoading = false;
    filterResponse: FilterResponse;
    sidebarVisible = false;
    private filterHandlerSubscription!: Subscription;
	@ViewChild('table') table: Table;

	constructor(
        private readonly router: Router,
		readonly standardRequestsFilterService: StandardRequestsFilterService,
		readonly standardRequestService: StandardRequestService,
        readonly standardRequestDetailService: StandardRequestDetailService,
		readonly newStandardRequestService: NewStandardRequestService,
		readonly utilsService: UtilsService,
        private readonly excelService: ExcelService,
		private readonly translate: TranslateService,
		private readonly datePipe: DatePipe
	) {
	    this.filterHandlerSubscription = this.standardRequestsFilterService.filterHandler().subscribe((filteredResponse: FilterResponse) => {
	        this.filterResponse = filteredResponse;
	    });
	}

	async ngOnInit(): Promise<void> {
	    await this.standardRequestsFilterService.search(true);
	}

	ngOnDestroy(): void {
	    if (this.filterHandlerSubscription) {
	        this.filterHandlerSubscription.unsubscribe();
	    }
	}

	goToDetail(queryId: string): void {
	    this.isLoading = true;
	    this.router.navigate([`/standard/request-detail/${queryId}`])
	        .catch(error => { console.error(error); })
	        .finally(() => this.isLoading = false);
	}

	async exportToExcel(): Promise<void> {
	    //await this.excelService.exportList(this.filterResponse?.requestList);
	    const cols: ExcelRequestColumn[] = [
	        {
	            key: 'queryId',
	            i18n: 'MY_REQUESTS.QUERY_ID',
	            width:36
	        },
	        {
	            key: 'queryType',
	            i18n: 'FILTER.REQUEST_TYPE',
	            width: 24,
	            value: (r) => this.getQueryType(r)
	        },
	        {
	            key: 'personId',
	            i18n: 'MY_REQUESTS.ID',
	            width: 12
	        },
	        {
	            key: 'name',
	            i18n: 'FILTER.PERSON_ENTITY',
	            width: 34,
	            value: (r) => this.getFullName(r)
	        },
	        {
	            key: 'email',
	            i18n: 'MY_REQUESTS.EMAIL',
	            width: 24
	        },
	        {
	            key: 'phoneNumber',
	            i18n: 'MY_REQUESTS.PHONE',
	            width: 12
	        },
	        {
	            key: 'createdDateTimeUTC',
	            i18n: 'MY_REQUESTS.DATE',
	            width: 22,
	            value: (r) => this.datePipe.transform(r.createdDateTime, 'dd/MM/yyyy HH:mm:ss', 'Europe/Madrid')
	        },	
	        {
	            key: 'statusi18n',
	            i18n: 'MY_REQUESTS.STATUS',
	            width: 14,
	            value: (r) => this.translate.instant(`STATUS.${r.status}`)
	        },
	        {
	            key: 'substatusi18n',
	            i18n: 'MY_REQUESTS.SUBSTATUS',
	            width: 42,
	            value: (r) => this.translate.instant(`SUBSTATUS.${r.substatus}`)
	        }
	    ];

	    await this.excelService.exportList(this.filterResponse?.requestList, cols);
	}

	newRequest(): void {
	    this.newStandardRequestService.myForm.reset();
	    this.sidebarVisible = true;
	}

	globalSearch($event: Event): void {
	    const input = $event.target as HTMLInputElement;
	    this.standardRequestsFilterService.globalSearchSubject.next({ inputValue: input.value, inputFallBackReference: input });
	}

	checkFilterFieldHasValue(field: string): boolean {
	    return this.standardRequestsFilterService.form.get(field).value != null && this.standardRequestsFilterService.form.get(field).value != '';
	}

	showIfTableIsLoadingAndEmpty(): boolean {
	    return (this.filterResponse?.requestList?.length == 0 || this.filterResponse == null) && this.standardRequestsFilterService.isLoading;
	}

	onSortChange(event: any): void {
	    const field = event.field;
	    const order = event.order;
	    this.standardRequestsFilterService.setSortField(field, order);
	}

	async sync(): Promise<void> {
	    this.standardRequestsFilterService.resetSortField();
	    if (this.table) {
	        this.table.reset();	// refresh the table to show the new data
	    }
	    await this.standardRequestsFilterService.search(true);
	}

	private getFullName(r: PersonQuery): string {
	    const parts = [r.firstName, r.firstSurname, r.secondSurname].filter(p => !!p && String(p).trim().length > 0).map(p => String(p).trim());
	    return parts.join(' ');
	}

	protected getQueryType(pq: PersonQuery): string {
	    if (pq.belenderCircuit === 6) {
	        return this.translate.instant('FILTER.QUERY_TYPES.LEGAL_PERSON');
	    } else {
	        const personType = pq.personType === '00' ? 'GUARANTOR' : pq.personType;
	        return this.translate.instant(`PERSON_TYPES_STANDARD.${personType}`);
	    }
	}
}
